/**
 * @license
 * Copyright Alibaba.com All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

// tslint:disable no-any

import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { filter, mapTo } from 'rxjs/operators';
import { CascaderNzConfig, CascaderConfigKey, Cascader_CONFIG } from './cascader.config';

const isDefined = function(value?: any): boolean {
  return value !== undefined;
};

@Injectable({
  providedIn: 'root'
})
export class CascaderConfigService {
  private configUpdated$ = new Subject<keyof CascaderNzConfig>();

  /** Global config holding property. */
  private config: CascaderNzConfig;

  constructor(@Optional() @Inject(Cascader_CONFIG) defaultConfig?: CascaderNzConfig) {
    this.config = defaultConfig || {};
  }

  getConfigForComponent<T extends CascaderConfigKey>(componentName: T): CascaderNzConfig[T] {
    return this.config[componentName];
  }

  getConfigChangeEventForComponent(componentName: CascaderConfigKey): Observable<void> {
    return this.configUpdated$.pipe(
      filter(n => n === componentName),
      mapTo(undefined)
    );
  }

  set<T extends CascaderConfigKey>(componentName: T, value: CascaderNzConfig[T]): void {
    this.config[componentName] = { ...this.config[componentName], ...value };
    this.configUpdated$.next(componentName);
  }
}

// tslint:disable:no-invalid-this
// tslint:disable:no-any

const lowercaseFirstLetter = (s: string): string => {
  return s.charAt(0).toLowerCase() + s.slice(1);
};

export const trimComponentName = (componentName: string): CascaderConfigKey => {
  return lowercaseFirstLetter(
    componentName
      .replace('Cascader', '')
      .replace(/(Component|Directive|Service|ContainerComponent)$/g, '')
      .toLowerCase()
  ) as CascaderConfigKey;
};

/**
 * This decorator is used to decorate properties. If a property is decorated, it would try to load default value from
 * config.
 */
// tslint:disable-next-line:typedef
export function WithConfig<T>(innerDefaultValue?: T) {
  return function ConfigDecorator(target: any, propName: any, originalDescriptor?: TypedPropertyDescriptor<T>): any {
    const privatePropName = `$$__assignedValue__${propName}`;
    const componentName = trimComponentName(target.constructor.name) as CascaderConfigKey;

    if (Object.prototype.hasOwnProperty.call(target, privatePropName)) {
      console.warn(
        `The prop "${privatePropName}" is already exist, it will be override by ${componentName} decorator.`
      );
    }

    Object.defineProperty(target, privatePropName, {
      configurable: true,
      writable: true,
      enumerable: false
    });

    return {
      get(): T | undefined {
        const originalValue =
          originalDescriptor && originalDescriptor.get ? originalDescriptor.get.bind(this)() : this[privatePropName];

        if (isDefined(originalValue)) {
          return originalValue;
        }

        const componentConfig = this.nzConfigService.getConfigForComponent(componentName) || {};
        const configValue = componentConfig[propName];

        return isDefined(configValue) ? configValue : innerDefaultValue;
      },
      set(value?: T): void {
        if (originalDescriptor && originalDescriptor.set) {
          originalDescriptor.set.bind(this)(value);
        } else {
          this[privatePropName] = value;
        }
      },
      configurable: true,
      enumerable: true
    };
  };
}
